import * as React from "react";

import {
  type CustomRange,
  isCustomRange,
  type TimeRangeMode,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  type DateRange,
  DateRangePicker,
  Popover,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { formatCustomRange } from "./customRange";
import styles from "./CustomRangePopover.module.css";

export interface CustomRangePopoverProps {
  /** The active window — supplies both the chip's label and the picker's
   *  starting dates. */
  range: TimeRangeMode;

  /** Called ONLY when the user applies. Opening, editing and dismissing all
   *  leave the dashboard untouched. */
  onApply: (range: CustomRange) => void;
}

/**
 * The `Custom` chip and its date picker.
 *
 * The chip is a dialog trigger, NOT a toggle: pressing it changes nothing, so
 * `aria-pressed` would be a promise the control does not keep. Its active
 * state is carried by the label, which shows the applied dates. That is also
 * why it sits beside {@link ToggleButtonGroup} rather than inside it.
 */
export function CustomRangePopover({
  range,
  onApply,
}: CustomRangePopoverProps) {
  const [open, setOpen] = React.useState(false);
  const label = isCustomRange(range) ? formatCustomRange(range) : "Custom";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <button
            type="button"
            className={styles.chip}
            data-active={isCustomRange(range) ? "" : undefined}
          >
            {label}
          </button>
        }
      />
      <Popover.Content aria-label="Custom time range" className={styles.popup}>
        {/* Remounts on every open (the popup unmounts while closed), so the
         *  draft below always starts from the CURRENT window rather than
         *  whatever was typed and abandoned last time. */}
        <CustomRangeFields
          seed={TimeRange.fromMode(range).toDates()}
          onApply={(next) => {
            onApply(next);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      </Popover.Content>
    </Popover>
  );
}

interface CustomRangeFieldsProps {
  seed: { start: string; end: string };
  onApply: (range: CustomRange) => void;
  onCancel: () => void;
}

/**
 * The picker body: a date range plus Apply/Cancel.
 *
 * The draft lives here rather than in the picker, because committing is this
 * surface's decision — Apply is the only writer, and dismissing must leave the
 * dashboard untouched. `DateRangePicker` reports an unusable window as `null`,
 * so gating Apply needs no validation of its own; the picker explains a
 * reversed range itself.
 */
function CustomRangeFields({
  seed,
  onApply,
  onCancel,
}: CustomRangeFieldsProps) {
  const [draft, setDraft] = React.useState<DateRange | null>(seed);

  return (
    <div className={styles.fields}>
      <DateRangePicker value={draft} onChange={setDraft} />

      <div className={styles.actions}>
        <RobotoButton variant="secondary" onClick={onCancel}>
          Cancel
        </RobotoButton>
        <RobotoButton
          variant="primary"
          disabled={draft === null}
          onClick={() => {
            if (draft !== null) {
              onApply({ kind: "custom", start: draft.start, end: draft.end });
            }
          }}
        >
          Apply
        </RobotoButton>
      </div>
    </div>
  );
}
